// src/hooks/useAbielAudioPlayer.ts
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { AbielAudioService, revokeObjectUrl } from '@/components/audio/services/AbielAudioService';
import type { AbielVoice } from '@/components/audio/types/audioTypes';

/**
 * Minimal shape we rely on from a "prayer" object.
 * Your real type can include more fields; we only need id + content + title (optional).
 */
type PrayerLike = {
  id: string;
  content: string;
  title?: string;
};

type UseAbielAudioPlayerResult = {
  /** Toggle playback for a given prayer. If same prayer is playing, pause; otherwise (re)generate and play. */
  togglePrayer: (prayer: PrayerLike, voice?: AbielVoice) => Promise<void>;
  /** Is the specified prayer currently playing? */
  isPlayingPrayer: (id?: string | null) => boolean;
  /** Is TTS generation / handoff in progress? */
  isLoading: boolean;
};

export function useAbielAudioPlayer(): UseAbielAudioPlayerResult {
  const {
    play,            // expected to accept a URL (string). If your signature differs, see note below.
    pause,
    isPlaying,
    currentSessionId, // using currentSessionId instead of currentTrackId
    currentLanguage, // from your context; used to choose EN vs CN TTS text
  } = useAbielAudio();

  const [isLoading, setIsLoading] = useState(false);
  const lastUrlRef = useRef<string | null>(null);
  const lastPrayerIdRef = useRef<string | null>(null);

  // best-effort "is this the prayer" check based on context data
  const isPlayingPrayer = useCallback(
    (id?: string | null) => {
      if (!id) return false;
      // Prefer a strict currentSessionId if you have it; otherwise fall back to "isPlaying && same id"
      if (typeof currentSessionId === 'string') {
        return currentSessionId === id && !!isPlaying;
      }
      return lastPrayerIdRef.current === id && !!isPlaying;
    },
    [currentSessionId, isPlaying]
  );

  const stopCurrentUrl = useCallback(() => {
    if (lastUrlRef.current) {
      revokeObjectUrl(lastUrlRef.current);
      lastUrlRef.current = null;
    }
  }, []);

  // Clean up any object URL when this hook unmounts
  useEffect(() => {
    return () => stopCurrentUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePrayer = useCallback(
    async (prayer: PrayerLike, voice?: AbielVoice) => {
      if (!prayer?.id || !prayer?.content) return;

      // If same prayer is currently playing, pause it
      if (isPlayingPrayer(prayer.id)) {
        pause?.();
        return;
      }

      setIsLoading(true);
      try {
        // Generate a blob URL with the new binary-safe service
        // NOTE: currentLanguage comes from your audio context; it feeds the text processor
        const url = await AbielAudioService.generateAudio(prayer.content, {
          voice,
          language: currentLanguage,
          model: 'gpt-4o-mini-tts',
        });

        // Revoke any previous URL to avoid leaks, then remember the new one
        stopCurrentUrl();
        lastUrlRef.current = url;
        lastPrayerIdRef.current = prayer.id;

        /**
         * Hand the URL to your audio context to actually play.
         * The play function expects (url: string, sessionId: string)
         */
        await play(url, prayer.id);
      } finally {
        setIsLoading(false);
      }
    },
    [currentLanguage, isPlayingPrayer, pause, play, stopCurrentUrl]
  );

  return useMemo(
    () => ({ togglePrayer, isPlayingPrayer, isLoading }),
    [togglePrayer, isPlayingPrayer, isLoading]
  );
}

export default useAbielAudioPlayer;
